import { useEffect, useRef, useState } from "react";
import { Activity } from "../../../@types/activities.ts";
import { Activitystream } from "../../../api/activitystreams.ts";
import { useActivityShareOptions } from "./useActivityShareOptions.ts";
import { commentCount, useActivityPdf } from "./useActivityPdf.ts";
import { ActivityShareMenu, ShareAction } from "./ActivityShareMenuItems.tsx";

export type ShareDialog = null | "email_internal" | "email_external" | "pdf";

// Wires the share entries of an activity (context submenu and Teilen button)
// to their targets. Sharing as post and a PDF without comments happen right
// away; the e-mail forms and the PDF with its comment choice open a modal.
export function useActivityShare({
  activity,
  streamsFromActivity,
  isPublicView,
  shareActivity,
}: {
  activity: Activity;
  streamsFromActivity?: Activitystream[];
  isPublicView: boolean;
  shareActivity: () => void;
}) {
  const options = useActivityShareOptions({
    activity,
    streamsFromActivity,
    isPublicView,
  });
  const [dialog, setDialog] = useState<ShareDialog>(null);
  // A PDF run outlives the render it started in, so asking the state itself
  // would answer with what was open back then
  const openDialog = useRef<ShareDialog>(null);
  useEffect(() => {
    openDialog.current = dialog;
  }, [dialog]);
  const pdf = useActivityPdf({ activity, streams: streamsFromActivity ?? [] });

  async function generatePdfRightAway() {
    toastr.info(I18n.t("js.activitystreams.share_dialog.pdf_generating"));
    const result = await pdf.generate(false);
    // The share sheet needs a fresh tap (mobile). The modal asks for it, but
    // only when nothing else is open: a run that ends while an e-mail form is
    // up must not throw that form away, and the second modal would come up
    // without a backdrop (see Modal). The file is kept, so the next tap on the
    // entry shares it.
    if (result === "readyToShare") {
      if (openDialog.current === null) setDialog("pdf");
      else toastr.info(I18n.t("js.activitystreams.share_dialog.pdf_ready"));
    }
    if (result === "error")
      toastr.error(I18n.t("js.activitystreams.share_dialog.pdf_error"));
  }

  function onShare(action: ShareAction) {
    switch (action) {
      case "as_post":
        shareActivity();
        break;
      case "email_internal":
      case "email_external":
        setDialog(action);
        break;
      case "pdf":
        if (pdf.state === "generating") return;
        // A file that waits for a fresh tap goes out now instead of being
        // built a second time
        if (pdf.state === "readyToShare") {
          pdf.shareReadyFile();
          return;
        }
        if (commentCount(activity) > 0) {
          pdf.reset();
          setDialog("pdf");
        } else {
          generatePdfRightAway();
        }
        break;
    }
  }

  const menu: ActivityShareMenu = {
    options,
    emailRecommendationCount: {
      internal: activity.internal_email_recommendation_count,
      external: activity.external_email_recommendation_count,
    },
    pdfGenerating: pdf.state === "generating",
    onShare,
  };

  return { menu, dialog, setDialog, pdf };
}
